import type { AssistantContextItem } from "@/lib/use-assistant-context";
import type { TobisPersonRef, TobisTitle, TobisTitleFilters } from "@/lib/tobis";

// Kontext fuer den Assistenten (Section "tobis"): beschreibt den Vergleichs-
// Pool, den die Lab-Seite gerade zeigt, damit eine Frage wie "welche davon
// sind vergleichbar mit X" ohne Nachfrage beantwortet werden kann. Gleiche
// Budget-Logik wie match-result-assistant-context.ts (eigene Datei statt
// gemeinsames Modul: beide sind bewusst feature-lokal, siehe dort).

const MAX_CONTEXT_VALUE_LENGTH = 1_900;
// Die Lab-Seite ist nur eine von mehreren Seiten, die gleichzeitig Kontext
// registrieren koennten (Route + ggf. eine geoeffnete Titelseite) - ein Slot
// Reserve wie beim Match-Pendant.
const MAX_POOL_CONTEXT_ITEMS = 4;

function compact(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function limited(value: string): string {
  if (value.length <= MAX_CONTEXT_VALUE_LENGTH) return value;
  return `${value.slice(0, MAX_CONTEXT_VALUE_LENGTH - 1).trimEnd()}…`;
}

// Records aneinanderhaengen, bis das Zeichen-Budget eines Context-Items voll
// ist, dann im naechsten Item weiter - kein einzelner Titel wird abgeschnitten.
function packRecords(records: string[]): string[] {
  const chunks: string[] = [];
  let current = "";
  for (const rawRecord of records) {
    const record = limited(rawRecord);
    const next = current ? `${current}\n${record}` : record;
    if (next.length <= MAX_CONTEXT_VALUE_LENGTH) {
      current = next;
      continue;
    }
    if (current) chunks.push(current);
    current = record;
  }
  if (current) chunks.push(current);
  return chunks;
}

function filterSummary(filters: TobisTitleFilters): string {
  const parts = [
    filters.search.trim() ? `search "${filters.search.trim()}"` : null,
    filters.medium ? `medium ${filters.medium}` : null,
    filters.genres.length ? `genres ${filters.genres.join(", ")}` : null,
    filters.languages.length ? `languages ${filters.languages.join(", ")}` : null,
    filters.sources.length ? `sources ${filters.sources.join(", ")}` : null,
    filters.companies.length ? `production companies ${filters.companies.join(", ")}` : null,
    filters.yearFrom || filters.yearTo
      ? `year ${filters.yearFrom || "…"}–${filters.yearTo || "…"}`
      : null,
    filters.ratingMin ? `rating from ${filters.ratingMin}` : null,
    filters.votesMin ? `votes from ${filters.votesMin}` : null,
    filters.admissionsMin ? `DE admissions from ${filters.admissionsMin}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "no filters set (full catalog)";
}

function titleRecord(title: TobisTitle): string {
  return compact(
    [
      // [id] zuerst: SectionProfiles.kt (agent-service) instruiert den Assistenten, einen Titel als
      // `[Name](/tobis/titles/<id>)` zu verlinken - "using its id from a SearchTobisTitles/
      // GetTobisTitleDetail result... never invented". Ohne die id HIER sichtbar (der Assistent
      // beantwortet Fragen zum Result-Schritt fast immer direkt aus diesem Kontext, ohne zusaetzlich
      // ein Tool aufzurufen) hat er sich beobachtet eine erfunden (Marvin, 2026-08-07: Link zeigte auf
      // einen komplett anderen Titel) - derselbe "ohne id im Payload erfindet das Modell eine"-Fall wie
      // bei den kuratierten MCP-Operationen (agent-service/CLAUDE.md Regel 5).
      `[${title.id}]`,
      title.name,
      title.year ? `(${title.year})` : null,
      title.genres.length ? title.genres.join("/") : null,
      // null = kein TMDB-Treffer (Tonalitaet unbekannt), 0 = TMDB-Treffer ohne
      // Ueberlappung, >0 = Anzahl. Alle drei sind fuer die Antwort relevant -
      // "kein Treffer" ist kein "keine Ueberlappung".
      title.keywordMatchCount === null
        ? "no TMDB keyword coverage"
        : `${title.keywordMatchCount} keyword match${title.keywordMatchCount === 1 ? "" : "es"}`,
      title.imdbRating ? `IMDb ${title.imdbRating}` : null,
      title.admissionsDe ? `DE admissions ${title.admissionsDe}` : null,
      title.revenueUsd ? `revenue $${title.revenueUsd}` : null,
      title.budgetUsd ? `budget $${title.budgetUsd}` : null,
    ]
      .filter(Boolean)
      .join(" · "),
  );
}

export interface LabPitch {
  title: string;
  shortDescription: string;
  description: string;
  /** Freitext-Tonalitaetsbeschreibung (pitchHints im Editor). */
  tone: string;
  director: TobisPersonRef | null;
  writer: TobisPersonRef | null;
  cast: readonly TobisPersonRef[];
  /** Komprimierte KI-Zusammenfassung eines hochgeladenen Skript-PDFs, kein Volltext (siehe
   *  TobisScriptSummaryController im agent-service) - null, wenn kein Skript hochgeladen wurde. */
  scriptSummary: string | null;
}

function personName(person: TobisPersonRef | null): string | null {
  return person?.personName ?? person?.personId ?? null;
}

function pitchSummary(pitch: LabPitch): string | null {
  const parts = [
    pitch.title.trim() ? `title "${pitch.title.trim()}"` : null,
    pitch.shortDescription.trim() ? `logline: ${compact(pitch.shortDescription)}` : null,
    pitch.description.trim() ? `pitch: ${shortText(pitch.description, 600)}` : null,
    pitch.tone.trim() ? `tone: ${compact(pitch.tone)}` : null,
    personName(pitch.director) ? `director: ${personName(pitch.director)}` : null,
    personName(pitch.writer) ? `writer: ${personName(pitch.writer)}` : null,
    pitch.cast.length
      ? `cast: ${pitch.cast.map((p) => personName(p)).filter(Boolean).join(", ")}`
      : null,
    pitch.scriptSummary?.trim() ? `script summary: ${shortText(pitch.scriptSummary, 1200)}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join("; ") : null;
}

function shortText(value: string, maxLength: number): string {
  const normalized = compact(value);
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

export function buildLabAssistantContext(
  pitch: LabPitch,
  filters: TobisTitleFilters,
  // Die tatsaechlich aufgeloesten TMDB-Keyword-Strings (nicht die IDs): DAS
  // ist die Grundlage, nach der der Pool unten rangiert ist. Ohne diese Liste
  // explizit im Kontext weiss der Assistent nur DASS rangiert wurde, nicht
  // WORAUF - und kann eine Frage wie "warum ist X vergleichbar" nicht
  // beantworten, nur den Pool selbst nachschlagen.
  keywords: readonly string[],
  titles: readonly TobisTitle[],
  hasMore: boolean,
): AssistantContextItem[] {
  const pitchLine = pitchSummary(pitch);
  const context: AssistantContextItem[] = [
    {
      description: "the comparison pool the user is narrowing down in the Tobis lab",
      value:
        (pitchLine ? `The user's pitch: ${pitchLine}. ` : "") +
        (keywords.length
          ? `The pool below is ranked by overlap with these TMDB keywords, derived from the pitch: ` +
            `${keywords.join(", ")}. Titles with a higher keyword match count are closer matches. `
          : "No keywords have been derived yet, the pool below is unranked. ") +
        `${titles.length}${hasMore ? "+" : ""} titles currently in the pool, filtered by: ` +
        `${filterSummary(filters)}. Each record below starts with its [id] in brackets, then its known ` +
        "KPIs. If you link a title (`[Name](/tobis/titles/<id>)`), use that exact id, never invent one. " +
        "Use the title search/detail tools for anything not covered here (e.g. cast, exact keywords).",
    },
  ];

  const chunks = packRecords(titles.map(titleRecord));
  for (const [index, value] of chunks.entries()) {
    if (context.length >= MAX_POOL_CONTEXT_ITEMS) break;
    context.push({
      description: `comparison pool titles, part ${index + 1} of ${chunks.length}`,
      value,
    });
  }

  return context;
}
